// Tải song song dữ liệu Marketing / Trực page / Telesale / Sale cơ sở (API V2) cho kỳ đang xem và kỳ so sánh.
import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { getMarketingCampaignReportV2, getPagePerformanceV2, getSaleBranchPerformanceV2, getTelesalePerformanceV2 } from 'api/DashboardStatistics';
import { getAppointmentStatusSummary } from 'api/Appointment';
import { EMPTY_SNAPSHOT, fmtD, type Range, type Snapshot } from './insights';
import { sampleMarketing } from './sampleMarketing';

interface State {
  cur: Snapshot;
  prev: Snapshot;
  loading: boolean;
  /** Bộ phận tải lỗi ở kỳ hiện tại */
  errors: string[];
  updatedAt: Dayjs | null;
  branches: { id: number; name: string }[];
}

async function load(start: Dayjs, end: Dayjs, branch?: number) {
  const [page, tele, sale, mkt, appt] = await Promise.all([
    getPagePerformanceV2(fmtD(start), fmtD(end)),
    getTelesalePerformanceV2(fmtD(start), fmtD(end), branch),
    getSaleBranchPerformanceV2(fmtD(start), fmtD(end), branch),
    getMarketingCampaignReportV2(fmtD(start), fmtD(end)),
    getAppointmentStatusSummary({ date_from: fmtD(start), date_to: fmtD(end), branch_id: branch }),
  ]);
  // T_v2 không trả kết quả cuộc gọi — dùng chung customers.last_call_result của P_v2
  if (tele.data && !tele.data.call_result_summary.length && page.data?.call_result_summary) {
    tele.data.call_result_summary = page.data.call_result_summary.map(x => ({ name: x.name, total: x.count, ratio: x.ratio }));
  }
  const snap: Snapshot = {
    page: page.status && page.data ? page.data : null,
    tele: tele.status && tele.data ? tele.data : null,
    sale: sale.status && sale.data ? sale.data : null,
    mkt: mkt.status && mkt.data ? mkt.data : null,
    // phụ — lỗi thì panel trạng thái chỉ còn phần tại cơ sở, không báo lỗi cả trang
    appt: appt.status ? appt.data : null,
  };
  const errors = [!snap.page && 'Trực page', !snap.tele && 'Telesale', !snap.sale && 'Sale cơ sở', !snap.mkt && 'Chiến dịch quảng cáo'].filter(Boolean) as string[];
  return { snap, errors };
}

/** sampleMkt: khi server chưa có chiến dịch nào thì thay số liệu Marketing bằng dữ liệu mẫu (sampleMarketing.ts) */
export function useSalesData(range: Range, branch?: number, sampleMkt = false) {
  const [state, setState] = useState<State>({ cur: EMPTY_SNAPSHOT, prev: EMPTY_SNAPSHOT, loading: true, errors: [], updatedAt: null, branches: [] });
  const [tick, setTick] = useState(0);
  const keys = [fmtD(range.start), fmtD(range.end), fmtD(range.prevStart), fmtD(range.prevEnd)];
  const depKey = keys.join('|');

  useEffect(() => {
    let ignore = false;
    const [s, e, ps, pe] = depKey.split('|').map(d => dayjs(d));
    setState(st => ({ ...st, loading: true }));
    Promise.all([load(s, e, branch), load(ps, pe, branch)]).then(([cur, prev]) => {
      if (ignore) return;
      setState(st => {
        // giữ danh sách chi nhánh đầy đủ kể cả khi đang lọc 1 chi nhánh
        const map = new Map(st.branches.map(b => [b.id, b]));
        [...(cur.snap.tele?.branches || []), ...(cur.snap.sale?.branches || [])].forEach(b => map.set(b.id, b));
        return { cur: cur.snap, prev: prev.snap, loading: false, errors: cur.errors, updatedAt: dayjs(), branches: Array.from(map.values()) };
      });
    });
    return () => { ignore = true; };
  }, [depKey, branch, tick]);

  const reload = useCallback(() => setTick(t => t + 1), []);
  // đang tải lần đầu thì chưa biết server có chiến dịch hay không
  const mktEmpty = !!state.updatedAt && !state.cur.mkt?.campaigns?.length;
  const useSample = sampleMkt && mktEmpty;
  const shown = useMemo(() => {
    if (!useSample) return state;
    const [s, e, ps, pe] = depKey.split('|').map(d => dayjs(d));
    return {
      ...state,
      cur: { ...state.cur, mkt: sampleMarketing(s, e, state.cur.mkt) },
      prev: { ...state.prev, mkt: sampleMarketing(ps, pe, state.prev.mkt) },
      errors: state.errors.filter(x => x !== 'Chiến dịch quảng cáo'),
    };
  }, [state, useSample, depKey]);
  return { ...shown, reload, mktEmpty, mktSample: useSample };
}
